import React, { ReactNode, useEffect, useState } from 'react';
import './EventPage.scss';
import { useTranslation } from 'react-i18next';
import { Navigate, useParams } from 'react-router-dom';
import EventInfo from '../../components/EventInfo/EventInfo';
import EventRegistration from '../../components/EventRegistration/EventRegistration';
import Footer from '../../components/Footer/Footer';
import Header from '../../components/Header/Header';
import KiteCupContent from '../../components/KiteCup/Content';
import LocationsMap from '../../components/LocationsMap/LocationsMap';
import RecommendationsGallery from '../../components/RecommendationsGallery/RecommendationsGallery';
import SideHeaderText from '../../components/SideHeaderText/SideHeaderText';
import TextBlock from '../../components/TextBlock/TextBlock';
import { EventInstanceModel } from '../../data/EventInstanceModel';
import { EventTypeModel, isValidEventKey } from '../../data/EventTypeModel';
import { SYSTEM_DEVICE, useSystemDevice } from '../../hooks/useSystemDevice';

export interface Props {
  className?: string;
}

const EventPage: React.FC<Props> = (props) => {
  const scope = 'EventPage';

  const { t } = useTranslation();
  const systemDevice = useSystemDevice();
  const params = useParams<'eventKey'>();
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    EventInstanceModel.loadData()
      .then(() => {
        setIsLoading(false);
      })
      .catch((error) => {
        throw error;
      });
  }, []);

  if (!isValidEventKey(params.eventKey)) {
    return <Navigate to={'/'} replace />;
  }
  const eventType = EventTypeModel.find(params.eventKey);
  const className = [scope, `${scope}--${systemDevice}`, props.className]
    .filter((className) => className)
    .join(' ');

  const headerImageType =
    systemDevice === SYSTEM_DEVICE.DESKTOP ? 'landscape' : 'portrait';
  const headerImageSrc = eventType.getImageSrc(headerImageType, systemDevice);

  let headerTextBottom = eventType.attrs.headerTextBottom;
  if (!headerTextBottom && eventType.attrs.instructor) {
    headerTextBottom = `${t('with')} ${eventType.attrs.instructor.firstName} ${
      eventType.attrs.instructor.lastName
    }`;
  }

  const headerTextTop =
    eventType.attrs.headerTextTop ?? (isLoading ? '…' : eventType.weekDays);

  const galleryData = eventType.recommendedEvents.map((eventType) => {
    return {
      description: isLoading ? '…' : eventType.weekDays,
      systemDevice,
      imageSrc: eventType.getImageSrc(
        systemDevice === SYSTEM_DEVICE.DESKTOP ? 'landscape' : 'portrait',
        systemDevice,
      ),
      linkPath: eventType.linkPath,
      name: eventType.attrs.name,
    };
  });

  const eventInfo = (
    <EventInfo eventType={eventType} className={`${scope}-content`} />
  );
  let fullEventInfo: ReactNode;
  if (eventType.attrs.extraInfo) {
    const extraInfo = (
      <div className={`${scope}-extraInfo`}>
        <TextBlock
          className={`${scope}-halfContent`}
          subtitle={eventType.attrs.extraInfo.subtitle}
          text={eventType.attrs.extraInfo.text}
          title={eventType.attrs.extraInfo.title}
        />
      </div>
    );
    const extraImage = eventType.attrs.hasExtraImage ? (
      <img
        alt="Extra image"
        className={`${scope}-extraImage`}
        src={`${process.env.PUBLIC_URL}/media/events/${eventType.key}/${systemDevice}/extra-landscape.jpg`}
      />
    ) : (
      <div className={`${scope}-spacer`} />
    );
    fullEventInfo = (
      <>
        <div className={`${scope}-extraSection`}>
          {systemDevice === SYSTEM_DEVICE.DESKTOP ? (
            <>
              {extraImage}
              {extraInfo}
            </>
          ) : (
            <>
              {extraInfo}
              {extraImage}
            </>
          )}
        </div>
        {eventInfo}
      </>
    );
  } else {
    fullEventInfo = eventInfo;
  }

  const eventRegistration = eventType.attrs.hideRegistration ? (
    ''
  ) : (
    <div className={`${scope}-darkSection`}>
      <EventRegistration eventType={eventType} className={`${scope}-content`} />
    </div>
  );

  return (
    <div className={className}>
      <Header imageSrc={headerImageSrc}>
        <SideHeaderText
          bottom={headerTextBottom}
          heading={eventType.fullName}
          top={headerTextTop}
        />
      </Header>
      {eventType.key === 'kite-cup' ? (
        <KiteCupContent />
      ) : (
        <>
          {eventType.attrs.displayInfoBeforeRegistration ? (
            <>
              {fullEventInfo}
              {eventRegistration}
            </>
          ) : (
            <>
              {eventRegistration}
              {fullEventInfo}
            </>
          )}
          {eventType.attrs.mapInfo && (
            <LocationsMap
              animationData={eventType.attrs.animationData}
              text={eventType.attrs.mapInfo.text}
              title={eventType.attrs.mapInfo.title}
            />
          )}
        </>
      )}
      <div className={`${scope}-darkSection`}>
        <RecommendationsGallery
          className={`${scope}-content`}
          data={galleryData}
        />
      </div>
      {systemDevice !== SYSTEM_DEVICE.DESKTOP && (
        <img
          alt=""
          className={`${scope}-footerImage`}
          role="presentation"
          src={`${process.env.PUBLIC_URL}/media/footer-${systemDevice}.jpg`}
        />
      )}
      <Footer />
    </div>
  );
};

export default EventPage;
